import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import dedent from 'dedent';
import React, {
  ChangeEvent, ReactElement, Suspense, useEffect, useId, useRef, useState
} from 'react';
import { useForm } from 'react-hook-form';
import { AiOutlineLoading } from 'react-icons/ai';
import {
  FiArrowUp, FiLock, FiRotateCcw, FiTrash2, FiX
} from 'react-icons/fi';
import Markdown from 'react-markdown';
import { useSearchParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { z } from 'zod';
import 'react-toastify/dist/ReactToastify.css';

import APIGateway from '@/api';
import { Button } from '@/components/button/button';
import ChatBubble from '@/components/chatbubble/chatbubble';
import { Menu, MenuItem } from '@/components/dropdownMenu/dropdownMenu';
import { Icon } from '@/components/icon/icon';
import { Input } from '@/components/input/input';
import { Loader } from '@/components/loader/loader';
import Modal from '@/components/modal/modal';
import { useModelsQuery } from '@/hooks/queries/useModelsQuery';
import { useAuthContext } from '@/hooks/useAuthContext';
import { ChatMsgBasic, ChatMsgBasicHistory, ChatRoleEnum } from '@/types/chat';
import { UI_COOKIE, UICookie } from '@/types/cookie';
import {
  Model, ModelGenerationParamsDefault, ModelInvokeApiRes, ModelInvokeArgs
} from '@/types/model';
import {
  Prompt,
  PromptCreateRes,
  PromptGetArgs,
  PromptGetRes,
  PromptMsg,
  PromptMsgAuthorEnum,
  PromptReqIdentifier,
  PromptResIdentifier,
  PromptTypeEnum
} from '@/types/prompts';
import { cookie } from '@/utils/cookie';
import { parseParams } from '@/utils/searchParams';

import styles from './page.module.scss';

type SearchParams = {
  c?: string;
  d?: string;
  n: string;
  ta?: string;
  ty?: PromptTypeEnum;
  v?: string; // use version to determine if create or edit
};

// Zod validation schema
const promptMsgSchema = z.object( {
  name: z.string().min( 1, {
    error: 'Name is required'
  } ),
  role: z.string().min( 1, {
    error: 'Role is required'
  } ),
  content: z.string().min( 1, {
    error: 'Content is required'
  } )
} );

const formSchema = z.object( {
  promptMsgs: z.array( promptMsgSchema )
} );

type FormSchema = z.infer<typeof formSchema>;

function PromptCreatePage() {
  const NULL_TAGS_VAL = '*none*';

  const [ searchParams ] = useSearchParams();
  const {
    c: qPromptCreate = false.toString(),
    d: qPromptDescription = '',
    n: qPromptName,
    ta: qPromptTags = NULL_TAGS_VAL,
    ty: qPromptType = PromptTypeEnum.SIMPLE,
    v: qPromptVersion = null
  } = parseParams<SearchParams>( searchParams );
  const { sessionContext } = useAuthContext();
  const id = useId();

  const [ sChatInput, setSChatInput ] = useState( '' );
  const [ sChatModelName, setSChatModelName ] = useState<string>( '' );
  const [ sChatMsgHistory, setSChatMsgHistory ] = useState<ChatMsgBasicHistory>( [] );
  const [ sChatParamsDefault, setSChatParamsDefault ] = useState<ModelGenerationParamsDefault>( {} );
  const [ sConvoInProgressDisableEdits, setSConvoInProgressDisableEdits ] = useState<boolean>( false );
  const [ sIsLoadingPrompts, setSIsLoadingPrompts ] = useState( true );
  const [ sIsWaiting, setSIsWaiting ] = useState<boolean>( false );
  const [ sModels, setSModels ] = useState<Model[]>( [] );
  const [ _sPrompt, setSPrompt ] = useState<Prompt<PromptResIdentifier> | null>( null );
  const [ sPromptDescription, setSPromptDescription ] = useState<string>( qPromptDescription );
  const [ sPromptMsgs, setSPromptMsgs ] = useState<PromptMsg[]>( [] );
  const [ sPromptName, setSPromptName ] = useState<string>( qPromptName );
  const [ sPromptTags, setSPromptTags ] = useState<string>( qPromptTags );
  const [ sPromptType, setSPromptType ] = useState<PromptTypeEnum>( qPromptType );
  const [ sPromptVars, setSPromptVars ] = useState<Record<string, unknown>>( {} );
  const [ sPromptVersion, setSPromptVersion ] = useState<string | null>( qPromptVersion );
  const [ sPromptVarsExtracted, setSPromptVarsExtracted ] = useState<string[]>( [] );
  const [ sPromptVarsOrphaned, setSPromptVarsOrphaned ] = useState<string[]>( [] );
  const [ sShowModalCodeExample, setSShowModalCodeExample ] = useState<boolean>( false );
  const [ sShowModalInfo, setSShowModalInfo ] = useState<boolean>( true );
  const [ sShowModalPromptTypeChangeVerify, setSShowModalPromptTypeChangeVerify ] = useState<boolean>( false );
  const [ sSuppressShowInfo, setSSuppressShowInfo ] = useState<boolean>( false );

  const messagesEndRef = useRef<null | HTMLDivElement>( null );
  const messageInputRefs = useRef<Record<number, HTMLTextAreaElement | null>>( {} );

  // React Hook Form setup with Zod validation
  const {
    formState: { errors },
    trigger
  } = useForm<FormSchema>( {
    mode: 'onBlur',
    values: {
      promptMsgs: sPromptMsgs.map( msg => ( {
        name: msg.name,
        role: msg.role,
        content: msg.content
      } ) )
    },
    resolver: zodResolver( formSchema )
  } );

  /* HOOKS */
  useEffect( _getCookie, [] ); // get UI related cookie vals

  useEffect( () => {
    // Focus on first message textarea when message prompt loads
    if( sPromptType === PromptTypeEnum.MESSAGE && sPromptMsgs.length > 0 && !sIsLoadingPrompts ) {
      setTimeout( () => {
        messageInputRefs.current[0]?.focus();
      }, 100 );
    }
  }, [ sIsLoadingPrompts, sPromptType ] );

  useEffect( () => { // to load prompt
    if( qPromptCreate == true.toString() ) {
      // Only initialize if we don't already have prompt content
      if( sPromptMsgs.length === 0 || ( sPromptMsgs.length === 1 && !sPromptMsgs[0].content ) ) {
        _initializePrompts();
      }
    } else {
      _fetchData();
    }
  }, [ qPromptName, sPromptVersion ] );

  useEffect( () => { // on chat history determine if edits should be locked
    setSConvoInProgressDisableEdits( !!sChatMsgHistory.length );
  }, [ sChatMsgHistory ] );

  useEffect( () => { // on prompt change, check for prompt vars
    _effectPromptMsgChange();
  }, [ sPromptMsgs, sPromptType ] );

  const { data: rawModels, isPending: sIsLoadingModels } = useModelsQuery( { enabled: true } );
  useEffect( () => {
    if ( rawModels ) {
      setSModels( rawModels.toSorted( ( a, b ) => ( a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1 ) ) );
    }
  }, [ rawModels ] );

  /* HELPERS */

  const _fetchData = async() => {
    setSIsLoadingPrompts( true );

    try {
      const args: PromptGetArgs = {
        name: sPromptName,
        version: sPromptVersion?.length ? parseInt( sPromptVersion ) : null,
        tags: sPromptTags
      };
      console.log( 'fetching', args );
      const res: PromptGetRes = await APIGateway.prompts.promptGet( args );

      setSPrompt( res );

      // TODO: [jh 2025-07-07] consider using the entire prompt state object instead of individual properties. Shallow copy/comapre would work fine.
      setSPromptName( res.id.name );
      const version = res.id.version !== null
        ? res.id.version.toString()
        : res.id.version;
      setSPromptVersion( version );
      setSPromptTags(
        Array.isArray( res.id.tags )
          ? res.id.tags.join( ',' )
          : NULL_TAGS_VAL
      );
      setSPromptDescription( res.description );

      if( !!res.prompt_text ) {
        setSPromptType( PromptTypeEnum.SIMPLE );

        const newPromptMsgs = [
          {
            content: res.prompt_text,
            name: PromptMsgAuthorEnum.SYSTEM,
            role: PromptMsgAuthorEnum.SYSTEM
          }
        ];

        setSPromptMsgs( newPromptMsgs );
      }

      if( !!res.messages ) {
        setSPromptType( PromptTypeEnum.MESSAGE );

        setSPromptMsgs( res.messages );
      }
    } catch( e ) {
      console.error( 'Failed to fetch prompts:', e );
    } finally {
      setSIsLoadingPrompts( false );
    }
  };

  function _getCookie() { // funciton syntax for hoisting
    try {
      const { promptSuppressShowInfo }: UICookie = cookie.getCookie( UI_COOKIE );
      if( promptSuppressShowInfo !== undefined ) setSShowModalInfo( !promptSuppressShowInfo );
    /* eslint-disable no-empty */
    } catch( e ) {}
  }

  const _initializePrompts = () => {
    const newPromptMsgs: PromptMsg[] = [
      {
        content: '',
        name: PromptMsgAuthorEnum.SYSTEM,
        role: PromptMsgAuthorEnum.SYSTEM
      }
    ];

    setSPromptMsgs( newPromptMsgs );
    setSIsLoadingPrompts( false );
  };

  const _effectPromptMsgChange = async() => {
    // create new prompt vars state and orphaned state prompt var keys
    const newPromptVars: Record<string, unknown> = {};
    const newPrompVarsOrphaned: string[] = [];

    // find all variables in prompt and store for later use in replacement
    const extractedPromptVars: string[] = [];

    for( const sPromptMsg of sPromptMsgs ) {
      const foundVars = await _extractPromptTextVars( sPromptMsg.content );
      extractedPromptVars.push( ...foundVars );
    }

    extractedPromptVars.forEach( v => {
      newPromptVars[ v ] = undefined;
    } );
    setSPromptVarsExtracted( extractedPromptVars );

    /**
     * iterate over the current prompt var state. If a prop value exists write it to new prompt var state
     * handles:
     * 1) prompt var found (extracted undefined), state var has value => get written => state var exist w value
     * 2) prompt var found (extracted undefined), state var not value => not written => state var exist w/o value
     * 3) prompt var found (extracted undefined), state var not exist => get created => state var create w/o value
     * 4) no prompt var, state var has value => get written => state var exist w value ( added to orphaned prompt var )
     */

    Object.entries( sPromptVars ).forEach( ( [ sPromptVarName, sPromptVarVal ]: [ string, unknown ] ) => {
      if( sPromptVarVal ) {
        newPromptVars[ sPromptVarName ] = sPromptVarVal;

        const inExtracted = extractedPromptVars.includes( sPromptVarName );

        if( !inExtracted ) newPrompVarsOrphaned.push( sPromptVarName );
      }
    } );
    setSPromptVars( newPromptVars );
    setSPromptVarsOrphaned( newPrompVarsOrphaned );
  };

  // async to use in loop that respects async to maintain order when displaying vars form
  const _extractPromptTextVars = async( promptStr: string ): Promise<string[]> => {
    if ( !promptStr ) return [];

    const variableRegex = /({{[a-zA-Z_0-9]+}})/g;
    const foundVars = new Set<string>();

    let match;
    while ( ( match = variableRegex.exec( promptStr ) ) !== null ) {
      foundVars.add( match[1] );
    }

    return Array.from( foundVars );
  };

  /**
   * Replaces all instances of variables in sPromptVarsExtracted within sPrompt
   * with their corresponding values from sPromptVars.
   * If a variable is not found in sPromptVars, it is replaced with an empty string.
   */
  const _fillPromptVars = ( text: string ): string => {
    sPromptVarsExtracted.forEach( varName => {
      const value = sPromptVars[ varName ] ?? '';

      // Escape curly braces for use in RegExp
      const escapedVarName = varName.replace( /[.*+?^${}()|[\]\\]/g, '\\$&' );
      const regex = new RegExp( escapedVarName, 'g' );

      text = text.replace( regex, String( value ) );
    } );

    return text;
  };

  const _formatMessage = ( msg: ChatMsgBasic, i: number ) => {
    return (
      <div key={ i } >
        <ChatBubble type={ msg.role }>
          <Markdown>{ msg.content }</Markdown>
        </ChatBubble>
      </div>
    );
  };

  const _invokeModel = async( messages: ChatMsgBasicHistory ): Promise<ModelInvokeApiRes> => {
    const args: ModelInvokeArgs = {
      conversation: messages,
      generationParamsCustom: {},
      generationParamsDefault: sChatParamsDefault,
      modelName: sChatModelName!
    };

    return await APIGateway.models.modelInvoke( args );
  };

  const _flipPromptType = (): PromptTypeEnum => {
    return sPromptType === PromptTypeEnum.SIMPLE
      ? PromptTypeEnum.MESSAGE
      : PromptTypeEnum.SIMPLE;
  };

  /**
   * TODO: [jh 2025-07-25]
   * saving a version returns empty object
   * the newly created prompt number is auto-generated by mas and may not be sequential
   * need a way to get the newly created version to navigate to
   */
  const _hackGetNewVersion = async(): Promise<number> => {
    const args: PromptGetArgs = {
      name: sPromptName,
      tags: null,
      version: null
    };

    if( sPromptTags !== NULL_TAGS_VAL ) {
      args.tags = sPromptTags.split( ',' ).map( ( tag ) => tag.trim() ).join( ',' );
    }

    const promptRes: PromptGetRes = await APIGateway.prompts.promptGet( args );

    return promptRes.id.version!;
  };



  /* HANDLERS */
  const onChangeGenerationParamsDefault = ( name: keyof ModelGenerationParamsDefault, value: number ) => {
    const nextParams = {
      ...sChatParamsDefault,
      [ name ]: value
    };

    setSChatParamsDefault( nextParams );
  };

  const onChangePromptType = () => {
    // hide switch modal
    setSShowModalPromptTypeChangeVerify( false );

    // change prompt type
    const otherPromptType = _flipPromptType();
    setSPromptType( otherPromptType );

    // change prompt msg array for type. 1 simple => 1 msg, n msg => 1 simple
    const newPromptMsgs: PromptMsg[] = sPromptMsgs
      .slice( 0, 1 )
      .map( msg => ( { ...msg } ) );

    // if coming from msg prompt type convert prompt to system type
    if( otherPromptType === PromptTypeEnum.SIMPLE ) {
      newPromptMsgs[0].name = PromptMsgAuthorEnum.SYSTEM;
      newPromptMsgs[0].role = PromptMsgAuthorEnum.SYSTEM;
    }

    setSPromptMsgs( newPromptMsgs );
  };

  const onChangePromptVar = ( action: 'edit' | 'delete', promptName: string , promptVal?: unknown ) => {
    const newPromptVars = {
      ...sPromptVars
    };

    if( action === 'edit' ) newPromptVars[ promptName ] = promptVal;
    else if( action === 'delete' ) delete newPromptVars[ promptName ];

    setSPromptVars( newPromptVars );
  };

  const validatePromptMessages = async(): Promise<boolean> => {
    const result = await trigger( 'promptMsgs' );
    return result;
  };

  const hasFieldError = ( index: number, field: 'name' | 'role' | 'content' ): boolean => {
    return !!errors.promptMsgs?.[index]?.[field];
  };

  const onClickChat = async() => {
    // Validate prompt messages before sending
    const isValid = await validatePromptMessages();
    if( !isValid ) {
      return;
    }

    setSIsWaiting( true );

    try {
      const newChatMsgHistory: ChatMsgBasicHistory = [
        ...sChatMsgHistory
      ];

      // for new conversations push prompt first if provided
      if( sPromptMsgs.length && !sChatMsgHistory.length ) {
        // replace all prompt template variables with values
        const [ systemPrompt, ...msgPrompts ] = sPromptMsgs;

        if( systemPrompt ) {
          const resolvedSystemPrompt = _fillPromptVars( systemPrompt.content );

          const promptSystemMsg: ChatMsgBasic = {
            role: systemPrompt.role as ChatRoleEnum,
            content: resolvedSystemPrompt
          };
          newChatMsgHistory.push( promptSystemMsg );
        }

        for( const msg of msgPrompts ) {
          const resolvedMsgPrompt = _fillPromptVars( msg.content );

          const promptMsgMsg: ChatMsgBasic = {
            role: msg.role as ChatRoleEnum,
            content: resolvedMsgPrompt
          };
          newChatMsgHistory.push( promptMsgMsg );
        }
      }

      const newInputMessage: ChatMsgBasic = {
        role: ChatRoleEnum.USER,
        content: sChatInput
      };
      newChatMsgHistory.push( newInputMessage );

      setSChatMsgHistory( newChatMsgHistory );

      setTimeout( ()=>{
        messagesEndRef.current?.scrollIntoView( { behavior: 'smooth' } );
      }, 250 );

      setSChatInput( '' );

      const res: ModelInvokeApiRes = await _invokeModel( newChatMsgHistory );

      if( !res ) {
        throw new Error( 'no response returned' );
      }

      const newOutputMessage: ChatMsgBasic = {
        role: ChatRoleEnum.ASSISTANT,
        content: res.response
      };

      newChatMsgHistory.push( newOutputMessage );

      setSChatMsgHistory( newChatMsgHistory );

      setTimeout( () => {
        messagesEndRef.current?.scrollIntoView( { behavior: 'smooth' } );
      }, 250 );
    } catch( e ) {
      console.error( ( e as Error ).message );
      toast.error( ( e as Error ).message );
    }

    setSIsWaiting( false );
  };

  const onClickModalInfoClose = () => {
    setSShowModalInfo( !sShowModalInfo );

    if( sSuppressShowInfo ) {
      cookie.setCookieKeyVal<UICookie>( UI_COOKIE, 'promptSuppressShowInfo', sSuppressShowInfo );
    }
  };

  const onClickReset = () => setSChatMsgHistory( [] );

  const onPromptMsgAdd = () => {
    const addtlPromptMsg: PromptMsg = {
      content: '',
      name: PromptMsgAuthorEnum.SYSTEM,
      role: PromptMsgAuthorEnum.SYSTEM
    };

    const newPromptMsgs = [
      ...sPromptMsgs.map( msg => ( { ...msg } ) ),
      addtlPromptMsg
    ];

    const newIndex = newPromptMsgs.length - 1;
    setSPromptMsgs( newPromptMsgs );

    // Focus on the new message textarea
    setTimeout( () => {
      messageInputRefs.current[newIndex]?.focus();
    }, 100 );
  };

  const onPromptMsgChange = ( index: number, propName: keyof PromptMsg, propVal: unknown ) => {
    const newPromptMsg: PromptMsg = { ...sPromptMsgs[ index ], [ propName ]: propVal };

    const newPromptMsgs = [
      ...sPromptMsgs.slice( 0, index ).map( msg => ( { ...msg } ) ),
      newPromptMsg,
      ...sPromptMsgs.slice( index + 1 ).map( msg => ( { ...msg } ) )
    ];

    setSPromptMsgs( newPromptMsgs );
  };

  const onPromptMsgDelete = ( index: number ) => {
    // Prevent deletion if only one prompt remains
    if( sPromptMsgs.length <= 1 ) {
      return;
    }

    const newPromptMsgs = [
      ...sPromptMsgs.slice( 0, index ).map( msg => ( { ...msg } ) ),
      ...sPromptMsgs.slice( index + 1 ).map( msg => ( { ...msg } ) )
    ];

    setSPromptMsgs( newPromptMsgs );
  };

  const onSave = async() => {
    // Validate prompt messages before saving
    const isValid = await validatePromptMessages();
    if( !isValid ) {
      toast.error( 'Please fix validation errors before saving.' );
      return;
    }

    try {
      const prompt: Prompt<PromptReqIdentifier> = {
        id: {
          name: sPromptName,
          tags: null,
          version: null
        },
        author: sessionContext?.userId ?? 'unknown',
        description: sPromptDescription
      };
      if( sPromptTags !== NULL_TAGS_VAL ) {
        prompt.id.tags = sPromptTags.split( ',' ).map( ( tag ) => tag.trim() ).join( ',' );
      }

      if( sPromptType === PromptTypeEnum.MESSAGE ) {
        prompt.messages = sPromptMsgs;
      } else if( sPromptType === PromptTypeEnum.SIMPLE ) {
        prompt.prompt_text = sPromptMsgs[0].content;
      } else {
        console.error( `Unable to save. ${ sPromptType || 'undefined' } is not a recognized prompt type.` );
        toast( 'unable to save' );
        return;
      }

      // validate
      await APIGateway.prompts.promptValidate( { prompt } );

      // save
      const createRes: PromptCreateRes = await APIGateway.prompts.promptCreate( { prompt } );
      console.debug( 'createRes', createRes );
      toast.success( 'Prompt created successfully' );

      const nextPromptVersion = await _hackGetNewVersion();

      setSPromptVersion( nextPromptVersion.toString() ); // will also trigger hook to fetch data
    } catch( e ) {
      toast.error( `Failed to create prompt: ${ e instanceof Error ? e.message : 'Unknown error' }` );
    }
  };



  /* RENDERS */
  const renderModalCodeSample = (): ReactElement => (
    <Modal className={ styles.modal__code_sample }>
      <div className={ clsx( 'layout__row', 'pad' ) }>
        <h2>Python code using aidp-mas-sdk</h2>
        <FiX
          onClick={ () => setSShowModalCodeExample( !sShowModalCodeExample ) }
        />
      </div>
      <pre style={ { backgroundColor: '#f4f4f4', padding: '1rem', borderRadius: '8px' } }>
        <code>
          {
            dedent`
              from pwell.services.aidp.prompt_management.v1 import prompt_management_pb2_grpc
              from kanpai.adk.prompt_management import PromptManager
              from kanpai.adk.prompt_management.adapter.client_adapter import PromptManagementGrpcClient

              channel = grpc.insecure_channel("aidp-api:50051")
              stub = prompt_management_pb2_grpc.PromptManagementApiStub(channel)
              client = PromptManagementGrpcClient(stub)
              prompt_manager = PromptManager(client)
              prompt_manager.get_prompt(
                name="${ sPromptName }",
                version=${ sPromptVersion },
                tags=${ sPromptTags }
              )
            `
          }
        </code>
      </pre>
    </Modal>
  );

  const renderModalInfo = (): ReactElement => (
    <Modal className={ styles.modal__info }>
      <div className={ clsx( 'layout__row', 'pad' ) }>
        <h3>Prompt Disclaimer</h3>
      </div>
      <div className={ clsx( 'layout__col', 'pad' ) }>
        <p>Prompts are tracked using their name, version, and tags.</p>
        <p>When you make changes, a new version is created automatically — the original stays the same.</p>
        <p>If you change the tags, it starts a brand new prompt</p>
      </div>
      <div className={ clsx( 'layout__col', 'pad' ) }>
        <p>If you want to use the prompt test, select a model first to enable the chat.</p>
        <p>Once a conversation is started, all elements are locked until the chat is reset.</p>
      </div>
      <div className={ clsx( 'layout__row', 'pad' ) }>
        <div className={ clsx( 'layout__col', 'pad' ) }>
          <label>
            <input
              checked={ sSuppressShowInfo }
              id='suppressShowInfo'
              name='suppressShowInfo'
              onChange={ ( e ) => setSSuppressShowInfo( e.target.checked ) }
              type='checkbox'
            />
            <span>Don't show again</span>
          </label>
        </div>
        <div className={ clsx( 'layout__col', 'pad', 'align_right' ) }>
          <Button variant='primary' onClick={ onClickModalInfoClose } data-testid="close-prompt-disclaimer">Close</Button>
        </div>
      </div>
    </Modal>
  );

  const renderModalPromptTypeChangeVerify = (): ReactElement => (
    <Modal>
      <div className={ clsx( 'layout__row', 'pad' ) }>
        <h3>Are you sure?</h3>
      </div>
      <div className={ clsx( 'layout__row', 'pad' ) }>
        <p>Converting from a { sPromptType } prompt to a { _flipPromptType() } prompt will cause a total loss of data. Are you sure you want to do this?</p>
      </div>
      <div className={ clsx( 'layout__row', 'pad', 'flex_right' ) }>
        <Button variant='secondary' size='sm' onClick={ () => setSShowModalPromptTypeChangeVerify( !sShowModalPromptTypeChangeVerify ) }>Cancel</Button>
        <Button variant='primary' size='sm' onClick={ onChangePromptType }>Yes</Button>
      </div>
    </Modal>
  );

  const renderType = (): ReactElement => (
    <span>{ `${ sPromptType?.charAt( 0 ).toUpperCase() + sPromptType?.slice( 1 ) } Prompt` }</span>
  );

  const renderInfo = (): ReactElement => {
    const versions = [ sPromptVersion !== null ? sPromptVersion.toString() : '-' ];

    return (
      <>
        <span className={ clsx( styles.info_name, 'flex-right' ) }>
          <p>{ sPromptName }</p>
        </span>
        <span className={ clsx( 'version', styles.info_version ) }>
          Version
          <select
            value={ sPromptVersion ?? '-' }
            onChange={ () => undefined }
          >
            {
              versions.map( ( version: string, index: number ) => (
                <option value={ version } key={ `version-option-${ index }` }>
                  { version }
                </option>
              ) )
            }
          </select>
        </span>
        <span className={ styles.info_actions }>
          <Menu variant='select' value='actions'>
            <MenuItem
              value='show code sample'
              onClick={ () => setSShowModalCodeExample( !sShowModalCodeExample ) }
            />
          </Menu>
        </span>
      </>
    );
  };

  const renderProperties = (): ReactElement => (
    <>
      <Input
        label='Description'
        placeholder='Description'
        id='description'
        name='description'
        onChange={ e => setSPromptDescription( e.target.value ) }
        value={ sPromptDescription }
      />
      <Input
        label='Tags'
        placeholder='Tags'
        id='tags'
        name='tags'
        onChange={ e => setSPromptTags( e.target.value ) }
        value={ sPromptTags === null ? NULL_TAGS_VAL : sPromptTags }
      />
    </>
  );

  const renderPromptTitle = (): ReactElement => (
    <>
      <span>Prompt { sConvoInProgressDisableEdits && <FiLock /> }</span>
      <span>
        <Button
          variant='secondary'
          disabled={ sConvoInProgressDisableEdits }
          onClick={ () => setSShowModalPromptTypeChangeVerify( !sShowModalPromptTypeChangeVerify ) }
        >
          { `Convert to ${ _flipPromptType().charAt( 0 ).toUpperCase() + _flipPromptType().slice( 1 ) } Prompt` }
        </Button>
      </span>
    </>
  );

  const renderPromptSimple = (): ReactElement => {
    const simplePromptIndex = 0;
    const prompt = sPromptMsgs[ simplePromptIndex ];

    return (
      <textarea
        className={ clsx( hasFieldError( simplePromptIndex, 'content' ) && errors.promptMsgs && styles.input_error ) }
        disabled={ sConvoInProgressDisableEdits }
        id='prompt_msg'
        name='prompt_msg'
        onChange={ ( e: ChangeEvent<HTMLTextAreaElement> ) => onPromptMsgChange( simplePromptIndex, 'content', e.target.value ) }
        value={ prompt.content }
      />
    );
  };

  const renderPromptMessage = (): ReactElement => {
    const promptMsgs: ReactElement[] = [];

    for( const [ i, sPromptMsg ] of Object.entries( sPromptMsgs ) ) {
      const promptMsgIndex = parseInt( i );

      const onRoleSelect = ( value: string ) => {
        onPromptMsgChange( promptMsgIndex, 'role', value );
        if( hasFieldError( promptMsgIndex, 'role' ) ) {
          setTimeout( () => trigger( 'promptMsgs' ), 0 );
        }
      };

      promptMsgs.push(
        <div className={ clsx( 'layout__col', styles.item ) } key={ `prompt_msg_${ i }` }>
          <div className={ clsx( 'layout__row', styles.item_row ) }>
            <Input
              label='Name'
              placeholder='Name'
              id={ `name-${ promptMsgIndex }` }
              name='name'
              disabled={ sConvoInProgressDisableEdits }
              error={ hasFieldError( promptMsgIndex, 'name' ) && errors.promptMsgs ? ( errors.promptMsgs?.[promptMsgIndex]?.name?.message ?? ' ' ) : undefined }
              onBlur={ () => trigger( 'promptMsgs' ) }
              onChange={ ( e: ChangeEvent<HTMLInputElement> ) => {
                onPromptMsgChange( promptMsgIndex, 'name', e.target.value );
                if( hasFieldError( promptMsgIndex, 'name' ) ) {
                  setTimeout( () => trigger( 'promptMsgs' ), 0 );
                }
              } }
              value={ sPromptMsg.name }
            />
            <Menu
              label='Role'
              variant='select'
              disabled={ sConvoInProgressDisableEdits }
              value={ sPromptMsg.role || '-' }
              data-testid={ `role-${ promptMsgIndex }` }
            >
              <MenuItem value='-' onClick={ () => onRoleSelect( '' ) } />
              {
                Object.values( PromptMsgAuthorEnum ).map( ( val ) => (
                  <MenuItem key={ val } value={ val } onClick={ () => onRoleSelect( val ) } />
                ) )
              }
            </Menu>
            <button
              className={ styles.unstyle }
              disabled={ sConvoInProgressDisableEdits || sPromptMsgs.length === 1 }
              onClick={ () => onPromptMsgDelete( promptMsgIndex ) }
            >
              <FiX />
            </button>
          </div>
          <div className={ clsx( 'layout__col', styles.message_field ) }>
            <label className='auth_flow_label' htmlFor={ `message-${ promptMsgIndex }` }>Message</label>
            <textarea
              ref={ ( el ) => { messageInputRefs.current[promptMsgIndex] = el; } }
              className={ hasFieldError( promptMsgIndex, 'content' ) && errors.promptMsgs ? styles.input_error : '' }
              disabled={ sConvoInProgressDisableEdits }
              id={ `message-${ promptMsgIndex }` }
              name='prompt'
              onBlur={ () => trigger( 'promptMsgs' ) }
              onChange={ ( e: ChangeEvent<HTMLTextAreaElement> ) => {
                onPromptMsgChange( promptMsgIndex, 'content', e.target.value );
                if( hasFieldError( promptMsgIndex, 'content' ) ) {
                  setTimeout( () => trigger( 'promptMsgs' ), 0 );
                }
              } }
              value={ sPromptMsg?.content }
            />
          </div>
        </div>
      );
    }

    return (
      <div className={ clsx( 'layout__col', styles.message_prompt_items ) }>
        { promptMsgs }
        <div className='layout__row'>
          <Button
            variant='secondary'
            disabled={ sConvoInProgressDisableEdits }
            onClick={ () => onPromptMsgAdd() }
          >
            Add Message
          </Button>
        </div>
      </div>
    );
  };

  const renderPromptSave = (): ReactElement => (
    <Button variant='primary' onClick={ onSave }>Save Prompt As New Version</Button>
  );

  const renderConfig = (): ReactElement => {
    // generate labels and inputs
    const labels: ReactElement[] = [];
    const inputs: ReactElement[] = [];

    Object.entries( sPromptVars ).forEach( ( [ sPromptVarName, sPromptVarVal ]: [ string, unknown ], i: number ) => {
      // check if the keys in the prompt vars state is not in the prompt
      const orphanedPromptVar = sPromptVarsOrphaned.includes( sPromptVarName );

      const label = (
        <label className={ clsx( orphanedPromptVar && styles.missing ) } htmlFor='dscription' key={ `label-${ i }` }>
          {
            orphanedPromptVar && (
              <FiTrash2 onClick={ () => onChangePromptVar( 'delete', sPromptVarName ) } />
            )
          }
          { sPromptVarName }
        </label>
      );
      labels.push( label );

      const input = (
        <input
          className={ clsx( orphanedPromptVar && styles.missing, sConvoInProgressDisableEdits && styles.locked ) }
          disabled={ sConvoInProgressDisableEdits }
          key={ `input-${ i }` }
          onChange={ ( e: ChangeEvent<HTMLInputElement> ) => onChangePromptVar( 'edit', sPromptVarName, e.target.value ) }
          type='text'
          value={ sPromptVarVal as string || '' }
        />
      );
      inputs.push( input );
    } );

    return (
      <div className={ styles.config }>
        <div className={ clsx( 'layout__row', 'pad', styles.title ) }>
          <h4>Test Prompt { sConvoInProgressDisableEdits && <FiLock /> }</h4>
          <Button variant='secondary' icon={ <Icon icon={ FiRotateCcw } /> } onClick={ onClickReset }>
            Reset
          </Button>
        </div>
        <div className={ clsx( 'layout__row', 'pad' ) }>
          <Menu
            label='Model'
            variant='select'
            disabled={ sConvoInProgressDisableEdits }
            value={ sChatModelName || '-' }
            data-testid='model'
          >
            <MenuItem value='-' onClick={ () => setSChatModelName( '' ) } />
            {
              sModels.map( ( model: Model ) => (
                <MenuItem
                  key={ `model-${ model.name }` }
                  value={ model.name }
                  onClick={ () => setSChatModelName( model.name ) }
                />
              ) )
            }
          </Menu>
        </div>
        <div className={ clsx( 'layout__row', 'pad' ) }>
          <span className={ styles.item } >
            <label htmlFor='temperature'>Temperature: { sChatParamsDefault.temperature ?? 'default' }</label>
            <input
              disabled={ sConvoInProgressDisableEdits }
              id='temperature'
              max='1'
              min='0'
              onChange={ e => onChangeGenerationParamsDefault( 'temperature', Number( e.target.value ) ) }
              step='0.1'
              type='range'
              value={ sChatParamsDefault.temperature !== undefined ? sChatParamsDefault.temperature : 0 }
            />
          </span>
        </div>
        <div className={ clsx( 'layout__row', 'pad', styles.prompt_vars ) }>
          <div className={ clsx( 'layout__col', styles.labels ) }>
            { labels }
          </div>
          <div className={ clsx( 'layout__col', styles.inputs ) }>
            { inputs }
          </div>
        </div>
      </div>
    );
  };

  const renderConvo = (): ReactElement => {
    const disableInput = !sChatModelName;
    const disableSend = !sChatModelName || !sChatInput.trim();

    return (
      <>
        <div className={ clsx( 'layout__col', 'overflow_scroll', styles.chat_convo_messages ) }>
          {
            sChatMsgHistory.map( _formatMessage )
          }
          {
            sIsWaiting && (
              <ChatBubble type={ ChatRoleEnum.ASSISTANT }>
                <AiOutlineLoading className={ styles.loadgif }/>
              </ChatBubble>
            )
          }
          <div ref={ messagesEndRef } />
        </div>
        <div className={ clsx( 'layout__row', styles.chat_convo_input ) }>
          <input
            disabled={ disableInput }
            id={ id }
            onInput={ e => setSChatInput( e.currentTarget.value ) }
            onKeyDown={ e => ( e.key === 'Enter' ) && !disableSend && onClickChat() }
            placeholder='Enter Message Here.'
            type='text'
            value={ sChatInput }
          />
          <button disabled={ disableSend } onClick={ onClickChat }><FiArrowUp /></button>
        </div>
      </>
    );
  };

  const render = (): ReactElement => (
    <>
      { sShowModalInfo && renderModalInfo() }
      { sShowModalPromptTypeChangeVerify && renderModalPromptTypeChangeVerify() }
      { sShowModalCodeExample && renderModalCodeSample() }

      <div className={ clsx( 'layout__col', 'overflow_hide' ) }>
        <div className={ clsx( 'layout__row', styles.layout__type ) }>
          { renderType() }
        </div>
        <div className={ clsx( 'layout__row', styles.layout__info ) }>
          { renderInfo() }
        </div>
        <div className={ clsx( 'layout__row', 'fill', 'overflow_hide' ) }>
          <div className={ clsx( 'layout__col', styles.layout__col_left ) }>
            <div className={ clsx( 'layout__col', 'pad', styles.layout__properties ) }>
              { renderProperties() }
            </div>
            <div className={ clsx( 'layout__col', 'pad', styles.layout__prompt ) }>
              <div className={ clsx( 'layout__row', 'pad' ) }>
                { renderPromptTitle() }
              </div>
              <div className={ clsx( 'layout__row', 'pad', sPromptType == PromptTypeEnum.SIMPLE && 'fill' ) }>
                {
                  sPromptType === PromptTypeEnum.MESSAGE
                    ? renderPromptMessage()
                    : renderPromptSimple()
                }
              </div>
              <div className={ clsx( 'layout__row', 'pad', styles.save ) }>
                { renderPromptSave() }
              </div>
            </div>
          </div>
          <div className={ clsx( 'layout__col', 'overflow_hide', styles.layout__col_right ) }>
            <div className={ styles.layout__config }>
              { renderConfig() }
            </div>
            <div className={ clsx( 'layout__col', 'overflow_hide', styles.layout__chat_convo ) }>
              { renderConvo() }
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className={ clsx( 'page', 'no_overflow', styles.layout__prompt_page ) }>
      <ToastContainer />
      {
        ( sIsLoadingModels || sIsLoadingPrompts )
          ? <Loader />
          : render()
      }
    </div>
  );
}

export default function Page() {
  return (
    <Suspense fallback={ <Loader /> }>
      <PromptCreatePage />
    </Suspense>
  );
}
